import React, {useLayoutEffect, useRef, useState} from 'react';
import {Layout} from '@components/Layout';
import {Loading} from '@components/Loading';
import ProblemsOfTheDay from '@components/indexPage/ProblemsOfTheDay';
import Rush from '@components/indexPage/Rush';
import {useGameStyleEnabled} from '@utils/hooks/useGameStyleEnabled';
import {useSelector} from '@utils/hooks/common';
import NewestProblem from '@components/indexPage/NewestProblem';
import NeedRatingProblem from '@components/indexPage/NeedRatingProblem';
import RecommendedProblem from '@components/indexPage/RecommendedProblem';
import CollectionProgress from '@components/indexPage/CollectionProgress';
import TimeTrial from '@components/indexPage/TimeTrial';
import useScreenSize from '@utils/hooks/useScreenSize';
import TournamentInfo from '@components/indexPage/TournamentInfo';
import {FeatureName} from '@types';
import TrainingStatusWidget from '@components/indexPage/TrainingStatusWidget';
import {
    LevelProgress,
    levelProgressProps,
} from '@components/levelProgressBar/LevelProgress';
import {t} from 'i18next';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faArrowRight, faCircleInfo} from '@fortawesome/free-solid-svg-icons';
import {Link} from 'react-router';
import {useTranslation} from 'react-i18next';
import {useFetchScenarioHomeSummaryQuery} from '@goproblems/shared/api/scenarios';
import {getScenarioTexts} from '@utils/const/scenario';
import IndexSectionHeading from '@components/indexPage/IndexSectionHeading';
import InvasionProgressChart from '@components/indexPage/InvasionProgressChart';
import InvasionDifficultyProgress from '@components/indexPage/InvasionDifficultyProgress';

const InvasionProgressMobile: React.FC = () => {
    const {t} = useTranslation();
    const {data, isLoading} = useFetchScenarioHomeSummaryQuery();

    if (isLoading || !data || data.previews.length === 0) {
        return null;
    }

    return (
        <div className="index-home-feature index-home-card-list w-full">
            <IndexSectionHeading
                title={t('side_menu.categories.invasion_problems')}
                to="/invasions"
                actions={
                    <Link
                        to="/article/invasions"
                        className="index-home-header-icon-link"
                        aria-label={t('invasions.learn_more')}
                        title={t('invasions.learn_more')}>
                        <FontAwesomeIcon icon={faCircleInfo} />
                    </Link>
                }
            />

            <div className="card index-home-feature-card mb-2 p-2">
                <div className="relative h-16 w-full">
                    <InvasionProgressChart
                        progress={data.progress}
                        showLabels
                    />
                </div>
            </div>

            <div className="flex gap-2">
                {data.previews.map(preview => (
                    <div key={preview.id} className="min-w-0 flex-1">
                        <div className="card">
                            <Link
                                to={`/invasions/${preview.id}`}
                                style={{display: 'block'}}>
                                <img
                                    src={preview.imageUrl}
                                    alt={`${t(getScenarioTexts(preview.type).problemLabelKey)} #${preview.id}`}
                                    className="card-img"
                                    fetchPriority="high"
                                    style={{width: '100%', height: 'auto'}}
                                />
                            </Link>
                            <div className="px-2 py-1">
                                <InvasionDifficultyProgress summary={preview} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const INVASION_HEADER_HEIGHT = 40;
const INVASION_PREVIEW_PROGRESS_HEIGHT = 16;

const InvasionProgress: React.FC<{height: number}> = ({
    height,
}) => {
    const {t} = useTranslation();

    const previewSize = Math.max(
        height - INVASION_HEADER_HEIGHT - 26 - INVASION_PREVIEW_PROGRESS_HEIGHT,
        64
    );
    const title = t('invasions.invasion');
    const {data, isLoading} = useFetchScenarioHomeSummaryQuery();

    const renderPanel = (content?: React.ReactNode) => (
        <div
            style={{height}}
            className="index-home-feature min-h-0 w-full">
            <IndexSectionHeading
                title={title}
                to="/invasions"
                actions={
                    <Link
                        to="/article/invasions"
                        className="index-home-header-icon-link"
                        aria-label={t('invasions.learn_more')}
                        title={t('invasions.learn_more')}>
                        <FontAwesomeIcon icon={faCircleInfo} />
                    </Link>
                }
            />
            <div className="card index-home-feature-card min-h-0 flex-1">
                {content}
            </div>
        </div>
    );

    if (isLoading) {
        return renderPanel();
    }

    if (!data || data.previews.length === 0) {
        return null;
    }

    const preview = data.previews[0];

    return renderPanel(
        <div className="flex min-h-0 flex-1 items-stretch gap-3 p-3">
            <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">
                <div className="shrink-0 text-xs text-neutral-500 dark:text-neutral-400">
                    {t('levels.progress')}
                </div>
                <div className="relative min-h-0 flex-1">
                    <InvasionProgressChart
                        progress={data.progress}
                        showLabels
                    />
                </div>
            </div>

            {preview.imageUrl && (
                <div
                    className="index-home-invasion-preview flex max-w-[40%] shrink-0 self-center flex-col gap-1"
                    style={{width: previewSize}}>
                    <Link
                        to={`/invasions/${preview.id}`}
                        className="index-home-invasion-board block aspect-square overflow-hidden rounded-sm">
                        <img
                            src={preview.imageUrl}
                            alt={`${t(getScenarioTexts(preview.type).problemLabelKey)} #${preview.id}`}
                            className="block aspect-square w-full object-contain"
                            fetchPriority="high"
                        />
                    </Link>
                    <InvasionDifficultyProgress
                        summary={preview}
                    />
                </div>
            )}
        </div>
    );
};

const LevelProgressRow = ({userId}: Pick<levelProgressProps, 'userId'>) => {
    return (
        <div className="index-home-feature index-home-level-progress">
            <div className="index-home-section-heading">
                <h2>{t('index.level_progress')}</h2>
                <Link
                    to="/levels"
                    aria-label={`${t('index.see_all')} ${t('index.level_progress')}`}
                    className="index-home-section-link">
                    {t('index.see_all')}{' '}
                    <FontAwesomeIcon icon={faArrowRight} />
                </Link>
            </div>
            <div className="card index-home-feature-card index-home-level-progress-card h-36 px-2 py-1.5">
                <div className="relative flex min-h-0 flex-1 flex-col">
                    <LevelProgress
                        userId={userId}
                        maintainAspectRatio={false}
                        showGuides
                    />
                </div>
            </div>
        </div>
    );
};

const GameHomePage = React.lazy(() => import('./GameHomePage'));

const IndexPage = () => {
    const {user} = useSelector(state => state.auth);
    const featureFlags = useSelector(state => state.featureFlags);
    const {width} = useScreenSize();
    const recommendedRef = useRef<HTMLDivElement>(null);
    const [learningRowHeight, setLearningRowHeight] = useState<
        number | undefined
    >();
    const {isGameStyleEnabled, isLoading: siteStyleLoading} =
        useGameStyleEnabled();
    const shouldMeasureLearningRow =
        Boolean(user) &&
        width >= 992 &&
        !siteStyleLoading &&
        !isGameStyleEnabled;

    const tournamentEnable = featureFlags.some(
        featureFlag =>
            featureFlag.name === FeatureName.Tournament && featureFlag.enable
    );

    useLayoutEffect(() => {
        if (!shouldMeasureLearningRow) {
            setLearningRowHeight(undefined);
            return undefined;
        }

        const el = recommendedRef.current;
        if (!el) return undefined;

        const updateHeight = () => {
            const measuredEl =
                el.firstElementChild instanceof HTMLElement
                    ? el.firstElementChild
                    : el;
            const computedStyle = window.getComputedStyle(measuredEl);
            const verticalMargin =
                parseFloat(computedStyle.marginTop) +
                parseFloat(computedStyle.marginBottom);
            const nextHeight = Math.ceil(
                measuredEl.getBoundingClientRect().height + verticalMargin
            );
            if (nextHeight <= 0) return;

            setLearningRowHeight(currentHeight =>
                !currentHeight || Math.abs(currentHeight - nextHeight) > 1
                    ? nextHeight
                    : currentHeight
            );
        };

        updateHeight();
        const observer = new ResizeObserver(updateHeight);

        observer.observe(el);
        return () => {
            observer.disconnect();
        };
    }, [shouldMeasureLearningRow]);

    if (isGameStyleEnabled) {
        return (
            <React.Suspense
                fallback={
                    <Layout>
                        <Loading />
                    </Layout>
                }>
                <GameHomePage />
            </React.Suspense>
        );
    }

    if (siteStyleLoading) {
        return (
            <Layout>
                <Loading />
            </Layout>
        );
    }

    return (
        <Layout containerClassName="index-home-page">
            <div className="index-home flex w-full flex-col">
                <div className="index-home-top-row grid grid-cols-1 items-stretch gap-3 site-xl:grid-cols-2">
                    <div className="flex min-w-0">
                        <ProblemsOfTheDay />
                    </div>
                    <div
                        className={
                            'index-home-modes grid min-w-0 grid-cols-1 items-stretch gap-3 md:grid-cols-2'
                        }>
                        <div className="flex min-w-0">
                            <Rush stretch stretchRows showDailyHighScores />
                        </div>
                        <div className="flex min-w-0 flex-1 overflow-hidden">
                            <TimeTrial />
                        </div>
                    </div>
                </div>
                {user ? (
                    <>
                        <div className="index-home-secondary-row grid grid-cols-1 items-stretch gap-3 site-lg:grid-cols-2">
                            {user && (
                                <div className="min-w-0 index-page">
                                    <LevelProgressRow userId={user.id} />
                                </div>
                            )}
                            <div className="min-w-0 index-page">
                                {tournamentEnable && <TournamentInfo />}
                            </div>
                        </div>
                        {width < 992 ? (
                            <div className="index-home-mobile-learning flex flex-col">
                                <TrainingStatusWidget />
                                <InvasionProgressMobile />
                                <RecommendedProblem />
                            </div>
                        ) : (
                            <div className="index-home-learning-row">
                                <div
                                    className="index-home-learning-left"
                                    style={
                                        width < 1400
                                            ? {height: learningRowHeight}
                                            : undefined
                                    }>
                                    <div className="index-home-learning-left-inner">
                                        <div className="index-home-training min-w-0">
                                            <TrainingStatusWidget />
                                        </div>
                                        <div className="index-home-invasion min-w-0">
                                            {learningRowHeight && (
                                                <InvasionProgress
                                                    height={learningRowHeight}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="index-home-recommended min-w-0 overflow-hidden">
                                    <div ref={recommendedRef}>
                                        <RecommendedProblem
                                            fullWidth={width < 1400}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        <div className="index-home-guest-row flex flex-column flex-lg-row">
                            {tournamentEnable ? (
                                <>
                                    <div className="flex-1 index-page">
                                        <RecommendedProblem />
                                    </div>
                                    <div className="flex-1">
                                        <TournamentInfo />
                                    </div>
                                </>
                            ) : (
                                <RecommendedProblem />
                            )}
                        </div>
                    </>
                )}
                <CollectionProgress />
                <NewestProblem />
                {user && <NeedRatingProblem />}
            </div>
        </Layout>
    );
};

export default IndexPage;
